/**
 * `useTitleTypewriter` — animates a streamed thread title in character
 * by character.
 *
 * The backend pushes the final title in one shot via a `data-title`
 * transient part as soon as the title LLM resolves. To surface that
 * feel-good "the chat names itself while the assistant is talking"
 * animation the client treats the incoming string as a target and
 * reveals it one character per tick.
 *
 * The hook returns:
 *   - `displayed`     — the (possibly partial) title for rendering
 *   - `isAnimating`   — true while characters are still being added
 *   - `setTarget`     — call with a new title to (re)start the animation
 *   - `setImmediate`  — set the title without animating (used on load)
 */
import { useEffect, useRef, useState } from "react";

const DEFAULT_DELAY_MS = 30;

export interface TitleTypewriterState {
  displayed: string;
  isAnimating: boolean;
  setTarget: (title: string) => void;
  setImmediate: (title: string) => void;
}

export function useTitleTypewriter(
  initial: string,
  delayMs = DEFAULT_DELAY_MS,
): TitleTypewriterState {
  const [displayed, setDisplayed] = useState(initial);
  const [target, setTarget] = useState<string | null>(null);
  const charsRef = useRef(initial.length);

  useEffect(() => {
    if (target === null) return undefined;
    if (charsRef.current >= target.length) {
      setDisplayed(target);
      setTarget(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      charsRef.current += 1;
      setDisplayed(target.slice(0, charsRef.current));
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [target, displayed, delayMs]);

  return {
    displayed,
    isAnimating: target !== null,
    setTarget: (next: string) => {
      const trimmed = next.trim();
      if (!trimmed) return;
      charsRef.current = 0;
      setDisplayed("");
      setTarget(trimmed);
    },
    setImmediate: (next: string) => {
      const trimmed = next.trim();
      charsRef.current = trimmed.length;
      setDisplayed(trimmed);
      setTarget(null);
    },
  };
}
